/**
 * WorkerGroups — migrated from mock to live workerGroupsApi.
 *
 * CRUD for tenant-scoped worker groups. Groups are used for bulk
 * permission assignment and notification targeting.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, IconButton, Paper, Snackbar, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add as AddIcon, RefreshOutlined, EditOutlined, DeleteOutlined, Diversity3Outlined, PeopleOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { ApiError, systemApi, type Schema } from '../../../api';
import { ConfirmDialog } from '../../../components/primitives';
import GroupMembersDialog from './GroupMembersDialog';
import { translateApiError } from '../../../i18n/translateApiError';

type WorkerGroup = Schema<'WorkerGroupResponse'>;

interface FormState { name: string; description: string; }
const EMPTY_FORM: FormState = { name: '', description: '' };

const WorkerGroups: React.FC = () => {
  const { t } = useTranslation();
  const [groups, setGroups] = useState<WorkerGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WorkerGroup | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [toDelete, setToDelete] = useState<WorkerGroup | null>(null);
  const [membersOf, setMembersOf] = useState<WorkerGroup | null>(null);

  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await systemApi.workerGroupsApi.list() as WorkerGroup[];
      setGroups(data);
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? translateApiError(e, t) : t('org:workerGroups.toast.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    if (!q) return groups;
    return groups.filter(g => g.name.toLowerCase().includes(q) || (g.description ?? '').toLowerCase().includes(q));
  }, [groups, filter]);
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setFormError(null); setDialogOpen(true); };
  const openEdit = (g: WorkerGroup) => { setEditing(g); setForm({ name: g.name, description: g.description ?? '' }); setFormError(null); setDialogOpen(true); };
  const closeDialog = () => { setDialogOpen(false); setEditing(null); };

  const handleSave = async () => {
    if (!form.name.trim()) { setFormError(t('org:workerGroups.fields.nameRequired')); return; }
    setSaving(true);
    setFormError(null);
    try {
      if (editing) {
        await systemApi.workerGroupsApi.update(editing.id, { name: form.name, description: form.description || null });
        setToast(t('org:workerGroups.toast.updated', { name: form.name }));
      } else {
        await systemApi.workerGroupsApi.create({ name: form.name, description: form.description || null });
        setToast(t('org:workerGroups.toast.created', { name: form.name }));
      }
      closeDialog();
      await fetchAll();
    } catch (e) {
      setFormError(e instanceof ApiError ? translateApiError(e, t) : t('org:workerGroups.toast.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await systemApi.workerGroupsApi.delete(toDelete.id);
      setToast(t('org:workerGroups.toast.deleted', { name: toDelete.name }));
      setToDelete(null);
      await fetchAll();
    } catch (e) {
      setToast(e instanceof ApiError ? translateApiError(e, t) : t('org:workerGroups.toast.deleteFailed'));
      throw e;
    }
  };

  if (loading && groups.length === 0) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (error && groups.length === 0) {
    return <Box sx={{ py: 4 }}><Alert severity="error" action={<Button onClick={fetchAll} size="small">{t('common:actions.retry')}</Button>}>{error}</Alert></Box>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', py: '12px', width: '100%' }}>
      {/* Info banner — above header, same position as FaceTemplates/WorkerDocuments */}
      <Alert severity="info" sx={{ width: '100%', mb: 2 }} icon={<Diversity3Outlined />}>
        <AlertTitle>{t('org:workerGroups.banner.title')}</AlertTitle>
        {t('org:workerGroups.banner.body')}
      </Alert>

      {/* Header */}
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>{t('org:workerGroups.title')}</Typography>
          <Typography variant="body2" color="text.secondary">{t('org:workerGroups.subtitle')}</Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Tooltip title={t('common:actions.refresh')}><IconButton size="small" onClick={fetchAll} sx={{ border: '1px solid rgba(0,0,0,0.2)', borderRadius: 1, bgcolor: '#fff', '&:hover': { bgcolor: '#f5f5f5' } }}><RefreshOutlined fontSize="small" /></IconButton></Tooltip>
          <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={openAdd} sx={{ borderRadius: 1, textTransform: 'none', fontSize: '0.875rem' }}>{t('org:workerGroups.addButton')}</Button>
        </Stack>
      </Box>

      {/* Filter */}
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
        <TextField size="small" label={t('org:workerGroups.searchLabel')} value={filter} onChange={e => { setFilter(e.target.value); setPage(0); }} sx={{ width: 340 }} />
        <Box sx={{ flex: 1 }} />
        <TablePagination component="div" count={filtered.length} page={page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }} rowsPerPageOptions={[5, 10, 25]} sx={{ '.MuiTablePagination-toolbar': { pl: 0, minHeight: 36 } }} />
      </Box>

      {filter.trim() && (
        <Box sx={{ width: '100%', display: 'flex', gap: 0.75, mb: 1.5, alignItems: 'center' }}>
          <Chip label={t('org:workerGroups.searchChip', { query: filter.trim() })} size="small" onDelete={() => { setFilter(''); setPage(0); }} variant="outlined" color="primary" sx={{ borderRadius: 1, height: 24, fontSize: '0.75rem', fontWeight: 500 }} />
        </Box>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={{ width: '100%', borderRadius: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead sx={{ bgcolor: 'grey.100', '& .MuiTableCell-root': { fontWeight: 600 } }}>
            <TableRow>
              <TableCell>{t('org:workerGroups.table.name')}</TableCell>
              <TableCell>{t('org:workerGroups.table.description')}</TableCell>
              <TableCell align="center">{t('org:workerGroups.table.members')}</TableCell>
              <TableCell align="center">{t('org:workerGroups.table.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginated.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">{filter ? t('org:workerGroups.empty.noMatches') : t('org:workerGroups.empty.noResults')}</Typography>
              </TableCell></TableRow>
            ) : paginated.map(g => (
              <TableRow key={g.id} hover>
                <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{g.name}</Typography></TableCell>
                <TableCell><Typography variant="caption" color="text.secondary">{g.description ?? '—'}</Typography></TableCell>
                <TableCell align="center">
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setMembersOf(g)}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      minWidth: 'auto',
                      px: 1.5,
                    }}
                  >
                    {g.member_count}
                  </Button>
                </TableCell>
                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title={t('org:workerGroups.table.manageMembers')}>
                      <IconButton size="small" onClick={() => setMembersOf(g)}><PeopleOutline sx={{ fontSize: 16 }} /></IconButton>
                    </Tooltip>
                    <IconButton size="small" onClick={() => openEdit(g)}><EditOutlined sx={{ fontSize: 16 }} /></IconButton>
                    <IconButton size="small" onClick={() => setToDelete(g)} sx={{ '&:hover': { color: 'error.main' } }}><DeleteOutlined sx={{ fontSize: 16 }} /></IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 2 }}>{editing ? t('org:workerGroups.editDialogTitle', { name: editing.name }) : t('org:workerGroups.addDialogTitle')}</DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('org:workerGroups.fields.name')} required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label={t('org:workerGroups.fields.description')} multiline rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ py: 2 }}>
          <Button onClick={closeDialog} sx={{ textTransform: 'none' }}>{t('common:actions.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none' }}>
            {saving ? <CircularProgress size={18} /> : editing ? t('org:shared.saveChanges') : t('org:workerGroups.createSubmit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!toDelete}
        tone="danger"
        title={t('org:workerGroups.deleteTitle')}
        description={t('org:workerGroups.deleteDescription')}
        subject={toDelete && <Typography sx={{ fontWeight: 600 }}>{toDelete.name}</Typography>}
        confirmLabel={t('common:actions.delete')}
        confirmLoadingLabel={t('common:status.deleting')}
        onConfirm={handleDelete}
        onClose={() => setToDelete(null)}
      />

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="info" onClose={() => setToast(null)} sx={{ width: '100%' }}>{toast}</Alert>
      </Snackbar>

      <GroupMembersDialog
        group={membersOf}
        onClose={() => setMembersOf(null)}
        onChanged={fetchAll}
      />
    </Box>
  );
};

export default WorkerGroups;
